import { useQueryClient } from '@tanstack/react-query';
import { isTextUIPart, type UIMessage } from 'ai';
import { ArrowUp, CircleDashed, Pencil, Plus, Square, X } from 'lucide-react';
import {
  useEffect,
  memo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type RefObject,
} from 'react';

import type { ClinicianSuggestionViewT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { getFetchSuggestionChatEndpointQueryKey } from '@lotushealth/lotushealth_js_sdk/endpoints';

import { lotusDarkLogo } from '@/shared/assets';

import { useSuggestionChat } from '../api/useSuggestionChat';

interface SuggestionChatProps {
  userId: string;
  actionId: string;
  draftFingerprint: string;
  selectedActions: Array<{ id: string; title: string }>;
  onRemoveSelected: (id: string) => void;
}

function messageText(message?: UIMessage): string {
  if (!message) {
    return '';
  }
  return message.parts
    .filter(isTextUIPart)
    .map((part) => part.text)
    .join('');
}

const SuggestionMessage = memo(function SuggestionMessage({
  message,
  planUpdated,
}: {
  message: UIMessage;
  planUpdated: boolean;
}) {
  const text = messageText(message);
  if (message.role === 'user') {
    return (
      <div className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-[14px_14px_4px_14px] bg-secondary px-3 py-2 text-[13px] leading-relaxed">
        {text}
      </div>
    );
  }

  return (
    <div className="flex max-w-3xl items-start gap-2">
      <img
        src={lotusDarkLogo}
        alt=""
        className="mt-0.5 size-4 shrink-0 object-contain invert dark:invert-0"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/80">{text}</p>
        {planUpdated && (
          <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground">
            <Pencil className="size-3" aria-hidden="true" /> Plan updated
          </span>
        )}
      </div>
    </div>
  );
});

function SuggestionComposer({
  disabled,
  isBusy,
  onSend,
  onStop,
  selectedActions,
  onRemoveSelected,
}: {
  disabled: boolean;
  isBusy: boolean;
  onSend: (message: string) => void;
  onStop: () => void;
  selectedActions: Array<{ id: string; title: string }>;
  onRemoveSelected: (id: string) => void;
}) {
  const [draft, setDraft] = useState('');
  const message = draft.trim();
  const ButtonIcon = isBusy ? Square : ArrowUp;

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!message || disabled || isBusy) {
      return;
    }
    onSend(message);
    setDraft('');
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.nativeEvent.isComposing || event.key !== 'Enter' || event.shiftKey) {
      return;
    }
    event.preventDefault();
    submit();
  };

  return (
    <div className="flex flex-col gap-2">
      {selectedActions.length > 0 && (
        <div className="flex flex-wrap gap-1.5" aria-label="Selected actions">
          {selectedActions.map(({ id, title }) => (
            <span
              key={id}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-success-border bg-success-muted px-2.5 py-1 text-[11px] text-success-muted-foreground"
            >
              <span className="truncate">{title}</span>
              <button
                type="button"
                onClick={() => onRemoveSelected(id)}
                aria-label={`Remove ${title}`}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      <form
        onSubmit={submit}
        className="flex items-end gap-2.5 rounded-xl border bg-background px-2.5 py-2 transition-colors focus-within:border-ring"
      >
        <span className="relative mb-1 flex size-5 shrink-0 items-center justify-center text-muted-foreground">
          <CircleDashed className="absolute size-5" aria-hidden="true" />
          <Plus className="size-3" aria-hidden="true" />
        </span>
        <textarea
          rows={1}
          value={draft}
          disabled={disabled}
          aria-label="Message Cortex about this suggestion"
          placeholder={
            selectedActions.length ? 'How should these change?' : 'Or do something else…'
          }
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          className="field-sizing-content max-h-40 min-h-7 min-w-0 flex-1 resize-none bg-transparent py-1 text-[13px] leading-5 outline-none placeholder:text-muted-foreground disabled:cursor-wait"
        />
        <button
          type={isBusy ? 'button' : 'submit'}
          onClick={isBusy ? onStop : undefined}
          disabled={disabled || (!isBusy && !message)}
          aria-label={isBusy ? 'Stop response' : 'Send message'}
          className="mb-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background disabled:opacity-25"
        >
          <ButtonIcon className={isBusy ? 'size-3 fill-current' : 'size-4'} aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}

function Conversation({
  messages,
  open,
  isBusy,
  planUpdatedMessageId,
  endRef,
  onToggle,
}: {
  messages: UIMessage[];
  open: boolean;
  isBusy: boolean;
  planUpdatedMessageId?: string;
  endRef: RefObject<HTMLDivElement | null>;
  onToggle: () => void;
}) {
  if (messages.length === 0) {
    return null;
  }
  const showTyping = isBusy && messages.at(-1)?.role === 'user';

  return (
    <>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>
          Conversation · {messages.length} {messages.length === 1 ? 'message' : 'messages'}
        </span>
        <button
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          className="rounded-full px-2 py-0.5 hover:bg-muted hover:text-foreground"
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open && (
        <div className="flex max-h-[26rem] flex-col gap-3 overflow-y-auto px-0.5 py-1 pr-2">
          {messages.map((message) => (
            <SuggestionMessage
              key={message.id}
              message={message}
              planUpdated={message.id === planUpdatedMessageId}
            />
          ))}
          {showTyping && (
            <div
              className="flex items-center gap-2 text-muted-foreground"
              aria-label="Cortex is responding"
            >
              <img
                src={lotusDarkLogo}
                alt=""
                className="size-4 shrink-0 object-contain invert dark:invert-0"
                aria-hidden="true"
              />
              <span className="flex gap-1" aria-hidden="true">
                <span className="size-1.5 animate-pulse rounded-full bg-current" />
                <span className="size-1.5 animate-pulse rounded-full bg-current [animation-delay:150ms]" />
                <span className="size-1.5 animate-pulse rounded-full bg-current [animation-delay:300ms]" />
              </span>
            </div>
          )}
          <div ref={endRef} />
        </div>
      )}
    </>
  );
}

export function SuggestionChat({
  userId,
  actionId,
  draftFingerprint,
  selectedActions,
  onRemoveSelected,
}: SuggestionChatProps) {
  const queryClient = useQueryClient();
  const chat = useSuggestionChat(userId, actionId);
  const [threadOpen, setThreadOpen] = useState(true);
  const [planUpdatedMessageId, setPlanUpdatedMessageId] = useState<string>();
  const threadEnd = useRef<HTMLDivElement>(null);
  const latestMessages = useRef(chat.messages);
  const isBusy = chat.status === 'submitted' || chat.status === 'streaming';
  const latestText = messageText(chat.messages.at(-1));

  useEffect(() => {
    latestMessages.current = chat.messages;
  }, [chat.messages]);

  useEffect(() => {
    if (threadOpen) {
      threadEnd.current?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [chat.messages.length, isBusy, latestText, threadOpen]);

  const send = (message: string) => {
    setThreadOpen(true);
    void chat.sendMessage({ text: message }).then(async () => {
      const suggestionsQueryKey = ['/greenfield/care', userId, 'suggestions'];
      const historyQueryKey = getFetchSuggestionChatEndpointQueryKey(userId, actionId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: suggestionsQueryKey }),
        queryClient.invalidateQueries({ queryKey: historyQueryKey, refetchType: 'none' }),
      ]);
      const suggestion = queryClient
        .getQueryData<ClinicianSuggestionViewT[]>(suggestionsQueryKey)
        ?.find((item) => item.actionId === actionId);
      if (suggestion && JSON.stringify(suggestion.draft) !== draftFingerprint) {
        setPlanUpdatedMessageId(
          latestMessages.current.findLast((item) => item.role === 'assistant')?.id
        );
      }
    });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <Conversation
        messages={chat.messages}
        open={threadOpen}
        isBusy={isBusy}
        planUpdatedMessageId={planUpdatedMessageId}
        endRef={threadEnd}
        onToggle={() => setThreadOpen((open) => !open)}
      />
      {chat.historyError ? (
        <div className="flex items-center justify-between gap-3 text-[13px] text-destructive">
          <p role="alert">Conversation could not be loaded.</p>
          <button
            type="button"
            disabled={chat.isHistoryLoading}
            onClick={() => void chat.retryHistory()}
            className="shrink-0 rounded-md border border-destructive/40 px-2 py-1 hover:bg-destructive/10 disabled:opacity-50"
          >
            Retry
          </button>
        </div>
      ) : chat.error ? (
        <p role="alert" className="text-[13px] text-destructive">
          Message could not be sent. Try again.
        </p>
      ) : null}
      <SuggestionComposer
        disabled={chat.isHistoryLoading || Boolean(chat.historyError)}
        isBusy={isBusy}
        onSend={send}
        onStop={chat.stop}
        selectedActions={selectedActions}
        onRemoveSelected={onRemoveSelected}
      />
    </div>
  );
}
